/* eslint-disable @typescript-eslint/no-unused-vars */
import { LMSLessonStatus } from './Types'
import { isDebug, lmsRequired } from './App.config'
import { retrieveParamByName } from './Utils'

// Reference: https://rustici-docs.s3.amazonaws.com/crossdomain/3.x/API.html

type LMSLessonMode = 'normal' | 'browse' | 'review'
export const lmsCSS = 'padding: 2px 4px color: SteelBlue font-size: 12px border-left: 2px solid GoldenRod'

declare global {
    interface Window {
        SCORMDriver: {
            ConcedeControl: () => void
            CommitData: () => void
            SetScore: (
                intScore: number,
                intMaxScore: number,
                intMinScore: number
            ) => void
            LMSSetValue: (nam: string, val: number) => void
            ResetStatus: () => void
            SetReachedEnd: () => void
            SetPassed: () => void
            SetFailed: () => void
            SetBookmark: (val: string) => void
            SetSuspendData: (val: any) => void
            RecordTrueFalseInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            RecordMultipleChoiceInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            RecordFillInInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            RecordMatchingInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            RecordPerformanceInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            RecordSequencingInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            RecordLikertInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            RecordNumericInteraction: (
                strID: string,
                response: any,
                correct: boolean,
                correctResponse?: any,
                description?: string,
                weighting?: number,
                latency?: number,
                learningObjectiveID?: string
            ) => void
            GetScore: () => number | ''
            GetLessonMode: () => LMSLessonMode
            IsLoaded: () => boolean
            GetBookmark: () => string
            GetSuspendData: () => string
            GetStatus: () => any
            GetStudentID: () => any
            GetStudentName: () => string
        }
        GetSessionAccumulatedTime: () => number
    }
}



export const concedeControl = (): void => {
    const user = getStudentName()
    console.log(`%cGoodbye ${user}!`, lmsCSS)
    window.SCORMDriver.ConcedeControl()
}

export const getModuleScore = (): number => {
    let score: number
    const sessionScore = window.SCORMDriver.GetScore()
    isDebug && console.log(`%cLMS Session score: ${sessionScore}`, lmsCSS)
    if (sessionScore === '') {
        const suspendedScore = getSuspendData()
        isDebug && console.log(`%cLMSSuspended score: ${suspendedScore}`, lmsCSS)
        score = suspendedScore === '' ? 0 : Number(suspendedScore)
    } else {
        score = Number(sessionScore)
    }
    return score
}



export const getLessonMode = (): LMSLessonMode => {
    return window.SCORMDriver.GetLessonMode()
}

export const setModuleScore = (score: number): void => {
    setSessionScore(score)
    setSuspendData(score)
}

const setSessionScore = (score: number): void => {
    window.SCORMDriver.SetScore(score, 100, 0)
}

export const resetStatus = (): void => {
    window.SCORMDriver.ResetStatus()
}

const setPassed = (): void => {
    window.SCORMDriver.SetPassed()
    commitData()
}

// const setFailed = (): void => {
//     window.SCORMDriver.SetFailed()
//     commitData()
// }

export const setReachedEnd = (): void => {
    window.SCORMDriver.SetReachedEnd()
    commitData()
}

export const commitData = (): void => {
    isDebug && console.log('%cLMS Committing data...', lmsCSS)
    window.SCORMDriver.CommitData()
}

export const setBookmark = (val: string): void => {
    window.SCORMDriver.SetBookmark(val)
}

const setSuspendData = (val: any): void => {
    window.SCORMDriver.SetSuspendData(val)
    commitData()
}

export const getBookmark = (): string => {
    return window.SCORMDriver.GetBookmark()
}

const getSuspendData = (): string => {
    return window.SCORMDriver.GetSuspendData()
}

export const getStatus = (): any => {
    return global.window.SCORMDriver.GetStatus()
}

export const getStudentID = (): any => {
    return window.SCORMDriver.GetStudentID()
}

export const getStudentName = (): string => {
    return window.SCORMDriver.GetStudentName()
}

export const getSessionAccumulatedTime = (): number => {
    return window.GetSessionAccumulatedTime()
}

const isAvailable = (): boolean => {
    try {
        const lmsStatus: any = getStudentID()
        if (!lmsStatus) return false
        return true
    }
    catch { return false }
}

export const init = (): boolean => {
    const isLMSAvailable = isAvailable()
    if (!isLMSAvailable && lmsRequired) throw new Error('LMS not found.')
    if (isDebug && !isLMSAvailable) console.warn('LMS Unavailable')
    return isLMSAvailable
}

export const incrementScore = (scoreIncrement: number) => {
    try {
        const currentScore = getModuleScore()
        console.log(`%cUpdating score. Previous score:${currentScore}`, lmsCSS)
        const newScore: number = currentScore + scoreIncrement

        if (newScore <= 0)
            throw new Error(`Score must be greater than zero. ${newScore}`)

        setModuleScore(Math.min(newScore, 100))
        const modScore = getModuleScore()
        console.log(`%cNew score: ${modScore}`, lmsCSS)
    } catch (error) {
        console.error(error)
    }
}

export const setModulePassed = () => {
    const status = getStatus()
    if (status !== LMSLessonStatus.Passed) {
        // If not already Passed
        isDebug && console.log(`%cSetting LMS Lesson status to ${LMSLessonStatus.Passed}`, lmsCSS)
        setPassed()
    }
    setModuleScore(100)
    const newStatus = getStatus()
    const score = getModuleScore()
    console.log(`%cYour lesson status is:${newStatus}`, lmsCSS)
    console.log(`%cYour score is: ${score}`, lmsCSS)
}

interface CustomLaunchData {
    custom_param: string
}

const getCustomParamURL = () => {
    const url = window.parent.location.href
    const val = retrieveParamByName('CustomParams', url)
    if (val === null) throw new Error(`LMS Custom Launch Data missing.`)
    return val
}

export const getCustomLaunchData = async (): Promise<CustomLaunchData> => {
    isDebug && console.log('Retrieving LMS Custom Launch Data...')
    const customParamUrl = getCustomParamURL()
    return await fetch(customParamUrl)
        .then(response => {
            if (!response.ok) {
                throw new Error(response.statusText)
            }
            return response.json()
        })
        .catch(err => {
            throw new Error(`Error retrieving LMS Custom Launch Data: ${err}`)
        })
}